import { QuestionRadiogroupModel as Question } from "survey-core";
import {
  OnboardingApplicationStatus,
  OnboardingExamRegistryStatus,
  OnboardingLessonStatus,
  OnboardingQuiz,
  OnboardingQuizStatus,
  OnboardingRegistrationInfo,
  OnboardingTrainingCourse,
  OnboardingTypeExam,
  RoleEnum,
  Timeslot,
} from "../types";
import { orderBy, sortBy } from "lodash";
import { getToken, parseJwt } from "../utils";

export const convertCoursesResponse = (
  rawResponse: any,
  onboardingConfig: any,
  onboardingForm: any,
): { data: OnboardingTrainingCourse } => {
  const courseData = rawResponse.data.trainingHistory.courseResults.map((course: any) => {
    const { courseQuizResult } = course;
    const rawToken = getToken();
    const tokenData = rawToken ? parseJwt(rawToken) : {};
    const isReadonly =
      (onboardingConfig?.enableCandidateLogin && tokenData.role !== RoleEnum.CANDIDATE) ||
      ![
        OnboardingApplicationStatus.EDITING,
        // OnboardingApplicationStatus.FOLLOW_UP_REQUIRED,
      ].includes(onboardingForm?.applicationStatus);
    return {
      courseId: course.courseId,
      readonly: isReadonly,

      name: course.name,
      type: "ONBOARDING_TRAINING",
      code: course.name,
      status: course.courseStatus,
      lessons: course.lessonResults.map((lesson: any, index: number) => {
        const { lessonQuizResult } = lesson;
        const hasNext = index < course.lessonResults.length - 1;
        return {
          readonly: isReadonly,
          hasNext,
          lessonId: lesson.lessonId,
          name: lesson.name,
          code: lesson.code,
          courseName: course.name,
          status: lesson.lessonMaterialStatus,
          materialUpload: lesson.materialLink,
          ...(lessonQuizResult
            ? {
                quiz: {
                  readonly: isReadonly,
                  quizId: lessonQuizResult.quizId,
                  name: lessonQuizResult.name,
                  lessonId: lesson.lessonId,
                  courseId: course.courseId,
                  code: lessonQuizResult.code,
                  score: lessonQuizResult.score,
                  failedCount: 0,
                  passedCount: 0,
                  status:
                    lesson.lessonMaterialStatus !== OnboardingLessonStatus.COMPLETED
                      ? OnboardingQuizStatus.DISABLED
                      : lessonQuizResult.quizStatus,
                },
              }
            : {}),
          ...(courseQuizResult && !hasNext
            ? {
                courseQuiz: {
                  readonly: isReadonly,
                  quizId: courseQuizResult.quizId,
                  courseId: course.courseId,
                  name: courseQuizResult.name,
                  code: courseQuizResult.code,
                  score: courseQuizResult.score,
                  failedCount: 0,
                  passedCount: 0,
                  status: course.lessonResults.every(
                    (lesson: any) => lesson.lessonStatus === OnboardingLessonStatus.COMPLETED,
                  )
                    ? courseQuizResult.quizStatus
                    : OnboardingQuizStatus.DISABLED,
                },
              }
            : {}),
        };
      }),
      ...(courseQuizResult
        ? {
            quiz: {
              readonly: isReadonly,
              quizId: courseQuizResult.quizId,
              courseId: course.courseId,
              name: courseQuizResult.name,
              code: courseQuizResult.code,
              score: courseQuizResult.score,
              failedCount: 0,
              passedCount: 0,
              status: course.lessonResults.every(
                (lesson: any) => lesson.lessonStatus === OnboardingLessonStatus.COMPLETED,
              )
                ? courseQuizResult.quizStatus
                : OnboardingQuizStatus.DISABLED,
            },
          }
        : {}),
    };
  });
  const filteredCourses = courseData.filter(
    (course: OnboardingTrainingCourse) => course.lessons?.length,
  );
  return {
    data: filteredCourses,
  };
};
export const convertQuizResponse = (
  rawResponse: any,
  onboardingConfig: any,
  onboardingForm: any,
): {
  data: Pick<
    OnboardingQuiz,
    "questions" | "type" | "code" | "passingScore" | "maxFailCount" | "readonly"
  >;
} => {
  const rawToken = getToken();
  const tokenData = rawToken ? parseJwt(rawToken) : {};
  const isReadonly =
    (onboardingConfig?.enableCandidateLogin && tokenData.role !== RoleEnum.CANDIDATE) ||
    ![OnboardingApplicationStatus.EDITING].includes(onboardingForm?.applicationStatus);
  const quizTemplateData = rawResponse.data;
  const passingScore = quizTemplateData.passingScore;
  const questions = quizTemplateData.quizContent.pages[0].elements;
  return {
    data: {
      readonly: isReadonly,
      type: quizTemplateData.quizType,
      code: quizTemplateData.quizCode,
      passingScore,
      maxFailCount: Math.floor(((100 - passingScore ?? 0) / 100) * questions.length),
      questions,
    },
  };
};
export const convertExamRegistryResponse = (
  rawResponse: any,
  onboardingForm?: any,
): {
  data: OnboardingTypeExam[];
} => {
  const rawToken = getToken();
  const tokenData = rawToken ? parseJwt(rawToken) : {};
  const isReadonly =
    tokenData.sub !== onboardingForm?.formCreatorUserId ||
    ![OnboardingApplicationStatus.EDITING, OnboardingApplicationStatus.FOLLOW_UP_REQUIRED].includes(
      onboardingForm?.applicationStatus,
    );
  const data = rawResponse.data;
  const typeExams = sortBy(Object.keys(data), (x) => x.toLowerCase()).map((type) => ({
    type,
    exams: orderBy(
      data[type].map((exam: any) => {
        return {
          readonly: isReadonly,
          name: exam.examName,
          type: exam.examType,
          status: exam.status,
          score: exam.score ?? 0,
          id: exam.examCode,
          examResult: exam.examResult ? exam.examResult : undefined,
          registeredInfo: exam.registeredInfo ? exam.registeredInfo : undefined,
        };
      }),
      ["id"],
      ["asc"],
    ),
  }));
  return {
    data: typeExams,
  };
};

export const convertExamCentresResponse = (
  rawResponse: any,
): {
  data: OnboardingRegistrationInfo[];
} => {
  const data = rawResponse.data;
  return {
    data: data.map((item: any) => {
      return {
        examCentre: item,
      };
    }),
  };
};

export const convertExamTimeslotsResponse = (
  rawResponse: any,
): {
  data: Timeslot[];
} => {
  const data = rawResponse.data;
  return {
    data: data.map((item: any) => {
      return {
        name: `${item.examDate},${item.examStartTime}-${item.examEndTime}`,
        value: item.examScheduleId,
        remainingQuota: item.remainingQuota,
      };
    }),
  };
};
